import {ref, computed, watch, onBeforeUnmount} from 'vue';
import {storeToRefs} from 'pinia';
import {normalizeRotation} from '~/utils/math.js';

export function useSurfaceEditorElementDrag() {
  const surfaceEditorStore = useSurfaceEditorStore();
  const {scale, isContextBarAbove, surfaces} = storeToRefs(surfaceEditorStore);
  const surfaceEditorUtils = useSurfaceEditorUtils();

  const isDragging = ref(false);
  const showTooltip = ref(false);
  const activeHandle = ref('');
  const activeElement = ref(null);
  const startPos = ref({x: 0, y: 0});
  const cursor = ref({x: 0, y: 0});
  const dragChangesMade = ref(false);

  let rotationHandleDistanceFromCenter;

  const tooltipText = computed(() => {
    if (!activeElement.value) return '';

    if (activeHandle.value === SURFACE_EDITOR_DRAG_HANDLE.rotate) {
      return `${Math.round((activeElement.value.rotation && normalizeRotation(activeElement.value.rotation)) || 0)}°`;
    }
    if (activeHandle.value === SURFACE_EDITOR_DRAG_HANDLE.all) {
      return `left:${Math.round(activeElement.value.x)} top:${Math.round(activeElement.value.y)}`;
    }
    return `w:${Math.round(activeElement.value.width)} h:${Math.round(activeElement.value.height)}`;
  });

  const tooltipStyle = computed(() => {
    return {
      left: `${cursor.value.x + 25}px`,
      top: `${cursor.value.y + 35}px`
    }
  });

  function getActiveElementFromStore() {
    if (!activeElement.value?.id) {
      return null;
    }

    return surfaceEditorUtils.findElementInSurface({
      elementId: activeElement.value.id,
      surfaces: surfaces.value
    })?.element || null;
  }

  function resetDragState() {
    showTooltip.value = false;
    isDragging.value = false;
    dragChangesMade.value = false;
    activeHandle.value = null;
    activeElement.value = null;
    startPos.value = {x: 0, y: 0};
    rotationHandleDistanceFromCenter = null;
    window.removeEventListener('pointermove', onDrag);
    window.removeEventListener('pointerup', stopDrag);
  }

  function getUseAlternateRotationHandlePosition(element) {
    const normalizedRotation = normalizeRotation(element.rotation);
    return isContextBarAbove.value
      ? (normalizedRotation > 150 && normalizedRotation < 210)
      : (normalizedRotation < 30 || normalizedRotation > 330);
  }

  const startDrag = (event, element, handleId) => {
    if (event.button !== 0) {
      return; // not left click
    }
    if (!element.draggable) {
      return;
    }

    // Capture pointer so drag continues even if pointer leaves the element
    event.target.setPointerCapture(event.pointerId);

    isDragging.value = true;
    dragChangesMade.value = false;
    activeHandle.value = handleId;
    activeElement.value = element;
    startPos.value = {
      x: event.clientX,
      y: event.clientY
    };

    if (handleId === SURFACE_EDITOR_DRAG_HANDLE.rotate) {
      const useAlternate = getUseAlternateRotationHandlePosition(element);
      rotationHandleDistanceFromCenter = useAlternate
        ? {
          x: element.width / 2,
          y: 0
        }
        : {
          x: 0,
          y: element.height / 2
        }
    }

    window.addEventListener('pointermove', onDrag);
    window.addEventListener('pointerup', stopDrag);
  }

  const onDrag = (event) => {
    if (!isDragging.value || !activeElement.value) {
      return;
    }

    const activeStoreElement = getActiveElementFromStore();

    if (!activeStoreElement) {
      stopDrag({skipFinalize: true});
      return;
    }

    activeElement.value = activeStoreElement;

    showTooltip.value = true;
    cursor.value = {
      x: event.clientX,
      y: event.clientY
    }

    const dx = Math.round((event.clientX - startPos.value.x) / scale.value);
    const dy = Math.round((event.clientY - startPos.value.y) / scale.value);
    dragChangesMade.value = true;

    if (activeHandle.value === SURFACE_EDITOR_DRAG_HANDLE.rotate) {
      surfaceEditorStore.rotateElement({
        element: activeElement.value,
        dx,
        dy,
        handleDistanceFromCenter: rotationHandleDistanceFromCenter
      })
    } else {
      surfaceEditorStore.dragElement({
        element: activeElement.value,
        dragHandle: activeHandle.value,
        dx,
        dy
      });
    }
  }

  const stopDrag = ({skipFinalize = false} = {}) => {
    if (!isDragging.value) {
      return;
    }

    const activeStoreElement = skipFinalize ? null : getActiveElementFromStore();

    if (dragChangesMade.value && activeStoreElement) {
      if (activeHandle.value === SURFACE_EDITOR_DRAG_HANDLE.rotate) {
        surfaceEditorStore.rotateElement({element: activeStoreElement, finalize: true});
      } else {
        surfaceEditorStore.dragElement({element: activeStoreElement, finalize: true});
      }
    } else {
      surfaceEditorStore.cancelActiveInteraction();
    }

    resetDragState();
  }

  watch(
    () => getActiveElementFromStore(),
    (nextActiveElement) => {
      if (!isDragging.value || !activeElement.value) {
        return;
      }

      if (!nextActiveElement) {
        stopDrag({skipFinalize: true});
        return;
      }

      activeElement.value = nextActiveElement;
    }
  );

  onBeforeUnmount(() => {
    stopDrag({skipFinalize: true});
  });

  return {
    isDragging,
    showTooltip,
    tooltipText,
    tooltipStyle,
    startDrag,
    stopDrag
  };
}
